import { MolsonButton } from '@components/reusable'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useLeaveGuardStore } from '@/stores/leaveGuardStore'
import { useHandleUrlAroundQuotesType } from '../hooks/useHandleUrlAroundQuotesType'

export enum WizardSteps {
  OPTIONS = 0,
  OVERVIEW = 2,
  SERVICES = 1
}

type BreakdownButtonsProps = {
  activeStep: WizardSteps
  goPrevStep: () => void
  handleNext: () => void
  onDiscard?: () => void
  translationKeyPrefix: string
  savingLoading: boolean
  isNonEditableQuote?: boolean
}

export const BreakdownButtons = ({
  activeStep,
  goPrevStep,
  handleNext,
  onDiscard,
  translationKeyPrefix,
  savingLoading,
  isNonEditableQuote = false
}: BreakdownButtonsProps) => {
  const { t } = useTranslation('', {
    keyPrefix: translationKeyPrefix
  })

  const {
    attachmentId,
    equipmentId,
    quoteId: quoteIdParam,
    specId
  } = useParams()

  const location = useLocation()
  const navigate = useNavigate()

  const { baseFullPath } = useHandleUrlAroundQuotesType()
  const hasUnsavedChanges = useLeaveGuardStore(state => state.hasUnsavedChanges)
  const setHasUnsavedChanges = useLeaveGuardStore(
    state => state.setHasUnsavedChanges
  )

  const quoteId = quoteIdParam ?? specId

  const searchParams = new URLSearchParams(location.search)
  const editMode = searchParams.get('edit')

  const getButtonText = () => {
    if (activeStep !== 2) {
      return t('next')
    }

    if (editMode) {
      return t('save')
    }

    if (attachmentId && equipmentId) {
      return t('addToMachine')
    }

    if (specId) {
      return t('addToSpec')
    }

    return t('addToQuote')
  }

  const handleSaveAction = () => {
    if (activeStep === WizardSteps.OVERVIEW) {
      setHasUnsavedChanges(false)
    }

    handleNext()
  }

  const handleOnDiscard = () => {
    if (!hasUnsavedChanges) {
      onDiscard?.()
    }
    navigate(`${baseFullPath}${quoteId}`)
  }

  return (
    <>
      <MolsonButton
        onClick={() => {
          if (activeStep === 0) {
            handleOnDiscard()
          } else {
            goPrevStep()
          }
        }}
        sx={{
          flex: 1,
          fontSize: {
            lg: '16px',
            sm: '12px'
          }
        }}
        disabled={isNonEditableQuote}
        color="secondary"
        variant="outlined"
      >
        {t('back')}
      </MolsonButton>
      <MolsonButton
        sx={{
          fontSize: {
            lg: '16px',
            sm: '12px'
          },
          minWidth: '50%'
        }}
        disabled={isNonEditableQuote}
        onClick={handleSaveAction}
        loading={savingLoading}
        variant="contained"
      >
        {getButtonText()}
      </MolsonButton>
    </>
  )
}
